const REVIEW_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const REVIEW_PHOTO_LIMIT = 5;
const REVIEW_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const REVIEW_SESSION_KEY = 'ac-review-session:v1';
const REVIEW_DRAFT_KEY = 'ac-review-draft:v1:';
const REVIEW_SELECTION_KEY = 'ac-review-draft-selection:v1';

function reviewTokenFromURL(url) {
  return new URLSearchParams(url.hash.slice(1)).get('review_token') || url.searchParams.get('review_token') || '';
}

function removeReviewToken(url) {
  url.searchParams.delete('review_token');
  const fragment = url.hash.slice(1);
  if (new URLSearchParams(fragment).has('review_token')) {
    url.hash = fragment.split('&').filter((part) => !new URLSearchParams(part).has('review_token')).join('&');
  }
  return url;
}

export class AcReviewForm {
  constructor(root) {
    if (root.dataset.acReviewFormReady === 'true') return;
    root.dataset.acReviewFormReady = 'true';
    this.root = root;
    this.endpoint = root.dataset.endpoint || '/apps/ac-messages/reviews';
    this.find = (name) => root.querySelector(`[data-review-${name}]`);
    this.form = this.find('fields');
    this.orders = [];
    this.drafts = new Map();
    this.activeKey = '';
    this.photos = [];
    this.photoLimit = REVIEW_PHOTO_LIMIT;
    this.reviewSession = '';
    this.submitting = false;
    this.loading = false;
    const currentURL = new URL(window.location.href);
    this.requestedOrderId = (currentURL.searchParams.get('order') || '').replace(/^gid:\/\/shopify\/Order\//, '');
    this.invitationToken = reviewTokenFromURL(currentURL);
    this.restoreSession();
    this.updateLoginLink();
    this.bindEvents();
    this.loadState();
  }

  bindEvents() {
    this.find('retry').addEventListener('click', () => this.loadState());
    this.find('order').addEventListener('change', () => {
      this.saveDraft();
      this.renderOrder(this.find('order').value);
    });
    this.find('item').addEventListener('change', () => {
      this.saveDraft();
      this.renderItem(this.find('item').value);
    });
    this.form.addEventListener('submit', (event) => this.submit(event));
    this.find('body').addEventListener('input', () => {
      this.find('body').setCustomValidity('');
      this.updateBodyCount();
    });
    this.find('name').addEventListener('input', () => this.find('name').setCustomValidity(''));
    this.form.addEventListener('change', (event) => {
      if (event.target.name === 'rating') this.updateStars();
      this.saveDraft();
    });
    this.form.addEventListener('input', () => this.saveDraft());
    this.find('login').addEventListener('click', () => this.saveDraft());
    this.root.querySelectorAll('[data-review-star]').forEach((star) => {
      star.addEventListener('pointerenter', (event) => {
        if (!this.submitting && event.pointerType !== 'touch') this.updateStars(Number(star.dataset.reviewStar));
      });
    });
    this.find('stars').addEventListener('pointerleave', () => this.updateStars());
    this.find('photo-input').addEventListener('change', () => this.addPhotos());
    this.find('photo-list').addEventListener('click', (event) => {
      const button = event.target.closest('[data-remove-photo]');
      if (!button || this.submitting) return;
      const index = Number(button.dataset.removePhoto);
      const [photo] = this.photos.splice(index, 1);
      if (photo) URL.revokeObjectURL(photo.url);
      this.renderPhotos();
      this.saveDraft();
      this.find('photo-error').hidden = true;
      const nextButton = this.find('photo-list').querySelectorAll('button')[index];
      (nextButton || this.find('photo-input')).focus();
    });
    this.find('another').addEventListener('click', () => {
      this.find('thanks').hidden = true;
      this.find('content').hidden = false;
      const nextOrder = this.orders.find((order) => order.items.some((item) => !item.reviewed));
      if (nextOrder) this.renderOrder(String(nextOrder.id));
      this.form.querySelector('input[name="rating"]')?.focus();
    });
    this.root.addEventListener('ac:review-form:refresh', () => this.loadState());
    window.addEventListener('hashchange', () => this.checkInvitationFromURL());
  }

  checkInvitationFromURL() {
    const url = new URL(window.location.href);
    const token = reviewTokenFromURL(url);
    if (!token || token === this.invitationToken || this.loading || this.submitting) return;
    this.saveDraft();
    this.activeKey = '';
    this.order = null;
    this.item = null;
    this.reviewSession = '';
    this.invitationToken = token;
    this.requestedOrderId = (url.searchParams.get('order') || '').replace(/^gid:\/\/shopify\/Order\//, '');
    this.updateLoginLink();
    this.loadState();
  }

  restoreSession() {
    if (this.invitationToken || this.root.dataset.customerSignedIn === 'true') return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(REVIEW_SESSION_KEY) || 'null');
      if (!saved || saved.kind !== 'invitation' || saved.expiresAt < Date.now()) return;
      if (this.requestedOrderId && saved.orderId !== this.requestedOrderId) return;
      this.reviewSession = typeof saved.token === 'string' ? saved.token : '';
    } catch {}
  }

  rememberSession(orderId) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(REVIEW_SESSION_KEY) || 'null');
      const expiresAt = Date.now() + 55 * 60 * 1000;
      sessionStorage.setItem(REVIEW_SESSION_KEY, JSON.stringify({
        kind: 'invitation',
        token: this.reviewSession,
        orderId: String(orderId || ''),
        expiresAt: saved?.token === this.reviewSession && Number.isFinite(saved.expiresAt) ? Math.min(saved.expiresAt, expiresAt) : expiresAt,
      }));
    } catch {}
  }

  updateLoginLink() {
    const returnURL = removeReviewToken(new URL(window.location.href));
    const loginURL = new URL(this.root.dataset.loginUrl || '/customer_authentication/login', window.location.origin);
    loginURL.searchParams.set('return_to', `${returnURL.pathname}${returnURL.search}${returnURL.hash}`);
    this.find('login').href = loginURL.toString();
  }

  setStatus(message) {
    this.find('status').textContent = message;
  }

  showError(message, retry = false) {
    const error = this.find('error');
    error.textContent = message;
    error.hidden = !message;
    this.find('retry').hidden = !retry;
    if (message) error.focus();
  }

  async request(path, body, multipart = false) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), multipart ? 120000 : 30000);
    try {
      const headers = { Accept: 'application/json' };
      if (!multipart) headers['Content-Type'] = 'application/json';
      if (multipart && this.reviewSession) headers['X-AC-Review-Session'] = this.reviewSession;
      const response = await fetch(`${this.endpoint.replace(/\/$/, '')}/${path}`, {
        method: 'POST', credentials: 'same-origin', cache: 'no-store', headers,
        body: multipart ? body : JSON.stringify(body), signal: controller.signal,
      });
      let data;
      try { data = await response.json(); } catch {
        throw new Error('We couldn’t open the review service. Please try again in a moment.');
      }
      if (!response.ok || data.ok !== true) {
        const error = new Error(data.error?.message || data.message || 'We couldn’t save that just yet. Please try again.');
        error.code = data.error?.code || (typeof data.error === 'string' ? data.error : '');
        error.status = response.status;
        throw error;
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('That took longer than expected. Your draft is still here. Please try again.');
      if (error instanceof TypeError) throw new Error('The connection was interrupted. Your draft is still here. Please try again.');
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async loadState() {
    if (this.loading || this.submitting) return;
    this.loading = true;
    this.saveDraft();
    this.root.setAttribute('aria-busy', 'true');
    this.showError('');
    this.setStatus('Opening your review…');
    this.find('access').hidden = true;
    this.find('content').hidden = true;
    this.find('thanks').hidden = true;
    try {
      let savedSelection;
      try {
        savedSelection = JSON.parse(sessionStorage.getItem(REVIEW_SELECTION_KEY) || 'null');
        if (savedSelection?.expiresAt < Date.now() || (this.requestedOrderId && savedSelection?.orderId !== this.requestedOrderId)) savedSelection = null;
      } catch {}
      const previousOrderId = this.order ? String(this.order.id) : savedSelection?.orderId || '';
      const previousItemId = this.item ? String(this.item.id) : savedSelection?.itemId || '';
      const data = await this.request('state', {
        orderId: this.requestedOrderId,
        ...(this.invitationToken ? { token: this.invitationToken } : {}),
        ...(this.reviewSession ? { reviewSession: this.reviewSession } : {}),
      });
      if (data.requiresLogin) {
        this.find('access').hidden = false;
        this.setStatus('');
        return;
      }
      this.defaultDisplayName = typeof data.displayName === 'string' ? data.displayName : '';
      this.orders = Array.isArray(data.orders) ? data.orders.filter((order) => order && Array.isArray(order.items)) : [];
      if (data.sessionToken && (this.invitationToken || this.reviewSession)) {
        this.reviewSession = data.sessionToken;
        this.rememberSession(data.selectedOrderId || this.orders[0]?.id);
      }
      if (this.invitationToken) {
        const currentURL = new URL(window.location.href);
        if (reviewTokenFromURL(currentURL) === this.invitationToken) {
          const cleanURL = removeReviewToken(currentURL);
          history.replaceState(history.state, '', `${cleanURL.pathname}${cleanURL.search}${cleanURL.hash}`);
        }
        this.invitationToken = '';
        this.updateLoginLink();
      }
      const orderPicker = this.find('order');
      orderPicker.replaceChildren(...this.orders.map((order) => new Option(order.label || 'Your order', String(order.id))));
      this.find('order-field').hidden = this.orders.length < 2;
      this.find('content').hidden = false;
      this.setStatus('');
      const preferredOrderId = previousOrderId || String(data.selectedOrderId || this.requestedOrderId || '');
      const selectedOrder = this.orders.find((order) => String(order.id) === preferredOrderId)
        || this.orders.find((order) => order.items.some((item) => !item.reviewed)) || this.orders[0];
      if (selectedOrder) {
        this.renderOrder(String(selectedOrder.id));
        if (String(selectedOrder.id) === previousOrderId && selectedOrder.items.some((item) => String(item.id) === previousItemId && !item.reviewed)) {
          this.renderItem(previousItemId);
        }
      } else {
        this.find('product').hidden = true;
        this.find('item-field').hidden = true;
        this.form.hidden = true;
        this.find('empty').textContent = 'There aren’t any orders available to review with this account yet.';
        this.find('empty').hidden = false;
      }
    } catch (error) {
      this.setStatus('');
      const expiredAccess = error.status === 401 || error.status === 403 || /TOKEN|SESSION|ACCESS/.test(error.code || '');
      this.showError(expiredAccess ? 'This review link has expired. Open your secure order link again or sign in to continue.' : error.message, !expiredAccess);
      if (expiredAccess) {
        this.find('access').hidden = false;
        this.reviewSession = '';
        try { sessionStorage.removeItem(REVIEW_SESSION_KEY); } catch {}
      }
    } finally {
      this.loading = false;
      this.root.setAttribute('aria-busy', 'false');
      this.checkInvitationFromURL();
    }
  }

  renderOrder(orderId) {
    this.order = this.orders.find((order) => String(order.id) === orderId);
    if (!this.order) return;
    this.find('order').value = orderId;
    const picker = this.find('item');
    picker.replaceChildren(...this.order.items.map((item) => {
      const option = new Option(`${item.title}${item.reviewed ? ' · Review received' : ''}`, String(item.id));
      option.disabled = Boolean(item.reviewed);
      return option;
    }));
    this.find('item-field').hidden = this.order.items.length < 2;
    const item = this.order.items.find((entry) => !entry.reviewed);
    this.renderItem(item ? String(item.id) : '');
  }

  renderItem(itemId) {
    this.item = this.order.items.find((item) => String(item.id) === itemId && !item.reviewed);
    this.showError('');
    this.find('photo-error').hidden = true;
    this.find('empty').hidden = Boolean(this.item);
    this.find('product').hidden = !this.item;
    this.form.hidden = !this.item;
    this.find('item').value = this.item ? String(this.item.id) : '';
    if (!this.item) {
      this.activeKey = '';
      this.find('empty').textContent = this.order.items.length
        ? 'You’ve shared your feedback on everything in this order. Thank you.'
        : 'There aren’t any items available to review in this order.';
      return;
    }
    this.find('order-label').textContent = this.order.label || 'Your order';
    this.find('product-title').textContent = this.item.title;
    this.find('product-variant').textContent = this.item.variantTitle || '';
    this.find('product-variant').hidden = !this.item.variantTitle;
    const image = this.find('product-image');
    image.hidden = true;
    image.removeAttribute('src');
    if (this.item.imageUrl) {
      try {
        const url = new URL(this.item.imageUrl, window.location.origin);
        if (url.protocol === 'https:') {
          image.src = url.href;
          image.hidden = false;
          image.onerror = () => { image.hidden = true; };
        }
      } catch {}
    }
    this.activeKey = `${this.order.id}:${this.item.id}`;
    const draft = this.drafts.get(this.activeKey) || this.restoreDraft();
    this.form.reset();
    this.find('body').setCustomValidity('');
    this.find('name').setCustomValidity('');
    this.find('body').value = draft?.body || '';
    this.find('name').value = draft?.displayName ?? this.defaultDisplayName;
    this.find('consent').checked = draft?.consent || false;
    this.form.querySelectorAll('[name="rating"]').forEach((radio) => { radio.checked = radio.value === draft?.rating; });
    this.photos = draft?.photos || [];
    this.submissionId = draft?.submissionId || crypto.randomUUID();
    this.updateStars();
    this.updateBodyCount();
    this.renderPhotos();
    if (draft?.photoCount && !this.photos.length) {
      this.find('photo-error').textContent = 'Your writing is saved. Please add your photos again.';
      this.find('photo-error').hidden = false;
    }
  }

  saveDraft() {
    if (!this.activeKey || !this.form) return;
    const draft = {
      body: this.find('body').value,
      displayName: this.find('name').value,
      consent: this.find('consent').checked,
      rating: this.form.querySelector('[name="rating"]:checked')?.value || '',
      photos: this.photos,
      submissionId: this.submissionId,
    };
    this.drafts.set(this.activeKey, draft);
    try {
      sessionStorage.setItem(REVIEW_DRAFT_KEY + this.activeKey, JSON.stringify({
        ...draft, photos: undefined, photoCount: this.photos.length,
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      }));
      sessionStorage.setItem(REVIEW_SELECTION_KEY, JSON.stringify({
        orderId: String(this.order.id), itemId: String(this.item.id),
        expiresAt: Date.now() + 24 * 60 * 60 * 1000,
      }));
    } catch {}
  }

  restoreDraft() {
    try {
      const draft = JSON.parse(sessionStorage.getItem(REVIEW_DRAFT_KEY + this.activeKey) || 'null');
      if (!draft || draft.expiresAt < Date.now()) return null;
      return {
        body: typeof draft.body === 'string' ? draft.body.slice(0, 5000) : '',
        displayName: typeof draft.displayName === 'string' ? draft.displayName.slice(0, 120) : this.defaultDisplayName,
        rating: /^[1-5]$/.test(draft.rating) ? draft.rating : '',
        consent: draft.consent === true,
        submissionId: typeof draft.submissionId === 'string' ? draft.submissionId : crypto.randomUUID(),
        photoCount: Number(draft.photoCount) || 0,
      };
    } catch { return null; }
  }

  updateStars(preview) {
    const rating = Number(this.form.querySelector('[name="rating"]:checked')?.value || 0);
    this.root.querySelectorAll('[data-review-star]').forEach((star) => {
      star.classList.toggle('is-active', Number(star.dataset.reviewStar) <= (preview ?? rating));
    });
    this.find('rating-label').textContent = rating ? `${rating} out of 5 stars` : 'Choose 1 to 5 stars. Every rating is welcome.';
  }

  updateBodyCount() {
    this.find('body-count').textContent = `${this.find('body').value.length.toLocaleString()} / 5,000`;
  }

  addPhotos() {
    const input = this.find('photo-input');
    const errors = [];
    for (const file of Array.from(input.files || [])) {
      if (this.photos.some((photo) => photo.file.name === file.name && photo.file.size === file.size && photo.file.lastModified === file.lastModified)) continue;
      if (this.photos.length >= this.photoLimit) { errors.push('You can keep up to 5 photos in total.'); break; }
      if (!REVIEW_PHOTO_TYPES.has(file.type)) { errors.push(`${file.name}: please choose a JPG, PNG or WebP photo.`); continue; }
      if (file.size > REVIEW_PHOTO_MAX_BYTES) { errors.push(`${file.name}: please choose a photo smaller than 10 MB.`); continue; }
      if (!file.size) { errors.push(`${file.name}: this photo is empty. Please choose another.`); continue; }
      this.photos.push({ file, url: URL.createObjectURL(file) });
    }
    input.value = '';
    this.find('photo-error').textContent = [...new Set(errors)].join(' ');
    this.find('photo-error').hidden = !errors.length;
    this.renderPhotos();
    this.saveDraft();
  }

  renderPhotos() {
    this.find('photo-list').replaceChildren(...this.photos.map((photo, index) => {
      const figure = document.createElement('figure');
      figure.className = 'ac-review-form__photo';
      const image = document.createElement('img');
      image.src = photo.url;
      image.alt = photo.file.name;
      image.width = 84;
      image.height = 84;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.removePhoto = String(index);
      remove.setAttribute('aria-label', `Remove ${photo.file.name}`);
      remove.textContent = '×';
      figure.append(image, remove);
      return figure;
    }));
  }

  showReceipt(alreadyReceived = false, reviewId = '') {
    this.item.reviewed = true;
    this.drafts.delete(this.activeKey);
    try { sessionStorage.removeItem(REVIEW_DRAFT_KEY + this.activeKey); } catch {}
    this.activeKey = '';
    this.photos.forEach((photo) => URL.revokeObjectURL(photo.url));
    this.photos = [];
    this.find('content').hidden = true;
    this.find('thanks-heading').textContent = alreadyReceived
      ? 'Your review is already with us.' : 'Thanks for sharing your experience.';
    this.find('thanks-copy').textContent = alreadyReceived
      ? 'We’ve already received a review for this item. This draft wasn’t submitted again.'
      : 'Your review has been received. It may take a little time to appear.';
    this.find('thanks').hidden = false;
    const manageLink = this.find('manage-link');
    if (manageLink) {
      const destination = new URL(manageLink.getAttribute('href'), window.location.origin);
      if (/^ac_[0-9a-f-]{36}$/i.test(reviewId)) destination.searchParams.set('review', reviewId);
      manageLink.href = destination.toString();
    }
    this.find('another').hidden = !this.orders.some((order) => order.items.some((item) => !item.reviewed));
    this.find('thanks').focus({ preventScroll: false });
    this.root.dispatchEvent(new CustomEvent('ac:review:submitted', { bubbles: true }));
  }

  async submit(event) {
    event.preventDefault();
    if (this.submitting || !this.order || !this.item) return;
    this.find('body').setCustomValidity(this.find('body').value.trim() ? '' : 'Please tell us a little about your experience.');
    this.find('name').setCustomValidity(this.find('name').value.trim() ? '' : 'Please choose a name to show with your review.');
    if (!this.form.reportValidity()) return;
    this.saveDraft();
    this.submitting = true;
    this.root.setAttribute('aria-busy', 'true');
    this.find('fieldset').disabled = true;
    this.find('order').disabled = true;
    this.find('item').disabled = true;
    this.find('submit').textContent = this.photos.length ? 'Uploading photos and sending…' : 'Sending your review…';
    this.setStatus('');
    this.showError('');
    const payload = new FormData();
    payload.set('orderId', String(this.order.id));
    payload.set('itemId', String(this.item.id));
    payload.set('rating', this.form.querySelector('[name="rating"]:checked').value);
    payload.set('body', this.find('body').value.trim());
    payload.set('displayName', this.find('name').value.trim());
    payload.set('consent', 'true');
    payload.set('submissionId', this.submissionId);
    if (this.reviewSession) payload.set('reviewSession', this.reviewSession);
    this.photos.forEach((photo) => payload.append('photos', photo.file, photo.file.name));
    try {
      const result = await this.request('submit', payload, true);
      this.showReceipt(result.duplicate === true, result.reviewId);
    } catch (error) {
      if (error.code === 'REVIEW_ALREADY_SUBMITTED') {
        this.showReceipt(true);
        return;
      }
      const expiredAccess = error.status === 401 || error.status === 403 || /TOKEN|SESSION|ACCESS/.test(error.code || '');
      const message = error.code === 'REVIEW_SUBMISSION_CONFLICT'
        ? 'Your earlier review was already received. These edits were not saved. Your draft is still here.'
        : error.message;
      this.showError(expiredAccess ? 'Your review access has expired. Your draft is still here. Sign in or reopen your secure order link to continue.' : message);
      if (expiredAccess) {
        this.find('access').hidden = false;
        this.reviewSession = '';
        try { sessionStorage.removeItem(REVIEW_SESSION_KEY); } catch {}
      }
    } finally {
      this.submitting = false;
      this.root.setAttribute('aria-busy', 'false');
      this.find('fieldset').disabled = false;
      this.find('order').disabled = false;
      this.find('item').disabled = false;
      this.find('submit').textContent = 'Submit my review →';
      this.checkInvitationFromURL();
    }
  }
}

function initializeReviewForms(scope = document) {
  scope.querySelectorAll('[data-ac-review-form]').forEach((root) => new AcReviewForm(root));
}

initializeReviewForms();
document.addEventListener('shopify:section:load', (event) => initializeReviewForms(event.target));
